/**
 * NavigatorX Analytics Dashboard
 * 
 * Comprehensive billing and usage analytics:
 * - MRR/ARR display
 * - Churn rate
 * - LTV calculation
 * - Conversion funnel
 * - Revenue breakdown
 * - User growth charts
 * 
 * @author Claude AI for TGE.LA
 * @version 1.0.0
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Users,
  UserPlus,
  UserMinus,
  Crown,
  Clock,
  RefreshCw,
  Loader2,
  AlertTriangle,
  Gift,
  Activity,
  BarChart3,
  PieChart
} from 'lucide-react';
import { toast } from 'sonner';
import {
  getBillingStats,
  getRevenueByMonth,
  SUBSCRIPTION_PRICE,
  type BillingStats
} from '../../../utils/services/navigatorXBillingService';
import { getNavigatorXStats, type NavigatorXStats } from '../../../utils/services/navigatorXService';

interface AnalyticsDashboardProps {
  embedded?: boolean;
}

export default function AnalyticsDashboard({ embedded = false }: AnalyticsDashboardProps) {
  const [loading, setLoading] = useState(true);
  const [billingStats, setBillingStats] = useState<BillingStats | null>(null);
  const [navStats, setNavStats] = useState<NavigatorXStats | null>(null);
  const [revenueByMonth, setRevenueByMonth] = useState<{ month: string; revenue: number; subscribers: number }[]>([]);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    setLoading(true);
    try {
      const [billing, nav, revenue] = await Promise.all([
        getBillingStats(),
        getNavigatorXStats(),
        getRevenueByMonth(6)
      ]);
      setBillingStats(billing);
      setNavStats(nav);
      setRevenueByMonth(revenue);
    } catch (error) {
      console.error('Error loading stats:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatPercent = (value: number) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center ${embedded ? 'py-12' : 'min-h-[400px]'}`}>
        <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
      </div>
    );
  }

  const stats = billingStats;
  const nav = navStats;

  return (
    <div className={embedded ? '' : 'p-6'}>
      {/* Header */}
      {!embedded && (
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">NavigatorX Analytics</h2>
            <p className="text-gray-500">Business metrics and insights</p>
          </div>
          <Button variant="outline" onClick={loadStats} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {/* MRR */}
        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-green-600 font-medium">Monthly Revenue</p>
                <p className="text-2xl font-bold text-green-700">{formatCurrency(stats?.mrr || 0)}</p>
                <p className="text-xs text-gray-500">MRR</p>
              </div>
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-green-600" />
              </div>
            </div>
            {stats?.revenueGrowth !== undefined && (
              <div className={`mt-2 flex items-center gap-1 text-xs ${stats.revenueGrowth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {stats.revenueGrowth >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                {formatPercent(stats.revenueGrowth)} vs last month
              </div>
            )}
          </CardContent>
        </Card>

        {/* ARR */}
        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-blue-600 font-medium">Annual Revenue</p>
                <p className="text-2xl font-bold text-blue-700">{formatCurrency(stats?.arr || 0)}</p>
                <p className="text-xs text-gray-500">ARR</p>
              </div>
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <BarChart3 className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Active Subscribers */}
        <Card className="bg-gradient-to-br from-purple-50 to-violet-50 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-purple-600 font-medium">Subscribers</p>
                <p className="text-2xl font-bold text-purple-700">{stats?.totalSubscribers || 0}</p>
                <p className="text-xs text-gray-500">Active paying</p>
              </div>
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                <Crown className="h-5 w-5 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Active Trials */}
        <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-amber-600 font-medium">Active Trials</p>
                <p className="text-2xl font-bold text-amber-700">{stats?.activeTrials || 0}</p>
                <p className="text-xs text-gray-500">In trial period</p>
              </div>
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Secondary Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        {/* LTV */}
        <Card>
          <CardContent className="p-4 text-center">
            <DollarSign className="h-5 w-5 text-gray-400 mx-auto mb-1" />
            <p className="text-xl font-bold text-gray-700">{formatCurrency(stats?.ltv || 0)}</p>
            <p className="text-xs text-gray-500">Lifetime Value</p>
          </CardContent>
        </Card>

        {/* Churn Rate */}
        <Card>
          <CardContent className="p-4 text-center">
            <UserMinus className="h-5 w-5 text-red-400 mx-auto mb-1" />
            <p className="text-xl font-bold text-gray-700">{(stats?.churnRate || 0).toFixed(1)}%</p>
            <p className="text-xs text-gray-500">Monthly Churn</p>
          </CardContent>
        </Card>

        {/* Conversion Rate */}
        <Card>
          <CardContent className="p-4 text-center">
            <TrendingUp className="h-5 w-5 text-green-400 mx-auto mb-1" />
            <p className="text-xl font-bold text-gray-700">{(stats?.conversionRate || 0).toFixed(1)}%</p>
            <p className="text-xs text-gray-500">Trial Conversion</p>
          </CardContent>
        </Card>

        {/* ARPU */}
        <Card>
          <CardContent className="p-4 text-center">
            <Users className="h-5 w-5 text-blue-400 mx-auto mb-1" />
            <p className="text-xl font-bold text-gray-700">${(stats?.averageRevenuePerUser || SUBSCRIPTION_PRICE).toFixed(2)}</p>
            <p className="text-xs text-gray-500">ARPU</p>
          </CardContent>
        </Card>

        {/* Failed Payments */}
        <Card className={stats?.failedPayments ? 'border-red-200' : ''}>
          <CardContent className="p-4 text-center">
            <AlertTriangle className={`h-5 w-5 mx-auto mb-1 ${stats?.failedPayments ? 'text-red-500' : 'text-gray-400'}`} />
            <p className={`text-xl font-bold ${stats?.failedPayments ? 'text-red-600' : 'text-gray-700'}`}>
              {stats?.failedPayments || 0}
            </p>
            <p className="text-xs text-gray-500">Failed Payments</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Revenue Chart */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Revenue Trend (6 Months)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {revenueByMonth.length === 0 ? (
              <p className="text-center text-gray-500 py-12">No revenue data</p>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <RechartsBarChart data={revenueByMonth} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#9ca3af" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#9ca3af" tickFormatter={(v: number) => `$${v}`} />
                  <Tooltip
                    formatter={(value: number) => [formatCurrency(value), 'Revenue']}
                    labelStyle={{ fontWeight: 600 }}
                    contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb' }}
                  />
                  <Bar dataKey="revenue" fill="#10b981" radius={[4, 4, 0, 0]} />
                </RechartsBarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* User Distribution */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <PieChart className="h-4 w-4" />
              User Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {/* Subscribers */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                  <span className="text-sm">Active Subscribers</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">{stats?.totalSubscribers || 0}</span>
                  <Badge className="bg-green-100 text-green-800 text-xs">
                    {nav?.totalUsers ? ((stats?.totalSubscribers || 0) / nav.totalUsers * 100).toFixed(0) : 0}%
                  </Badge>
                </div>
              </div>

              {/* Trial Users */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-blue-500" />
                  <span className="text-sm">Trial Users</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">{stats?.activeTrials || 0}</span>
                  <Badge className="bg-blue-100 text-blue-800 text-xs">
                    {nav?.totalUsers ? ((stats?.activeTrials || 0) / nav.totalUsers * 100).toFixed(0) : 0}%
                  </Badge>
                </div>
              </div>

              {/* Expired */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-gray-400" />
                  <span className="text-sm">Expired/Inactive</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">{nav?.expiredUsers || 0}</span>
                  <Badge className="bg-gray-100 text-gray-800 text-xs">
                    {nav?.totalUsers ? ((nav?.expiredUsers || 0) / nav.totalUsers * 100).toFixed(0) : 0}%
                  </Badge>
                </div>
              </div>

              {/* Invited */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-purple-500" />
                  <span className="text-sm">Invited (Pending)</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">{nav?.invitedUsers || 0}</span>
                  <Badge className="bg-purple-100 text-purple-800 text-xs">
                    {nav?.totalUsers ? ((nav?.invitedUsers || 0) / nav.totalUsers * 100).toFixed(0) : 0}%
                  </Badge>
                </div>
              </div>

              {/* Total */}
              <div className="pt-2 border-t flex items-center justify-between font-medium">
                <span className="text-sm">Total Users</span>
                <span>{nav?.totalUsers || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Engagement Stats */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Activity className="h-4 w-4" />
            Engagement Metrics
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <p className="text-xl font-bold text-gray-700">{nav?.totalLogins || 0}</p>
              <p className="text-xs text-gray-500">Total Logins</p>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <p className="text-xl font-bold text-gray-700">{(nav?.avgLoginsPerUser || 0).toFixed(1)}</p>
              <p className="text-xs text-gray-500">Avg Logins/User</p>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <p className="text-xl font-bold text-gray-700">{nav?.totalFavorites || 0}</p>
              <p className="text-xs text-gray-500">Total Favorites</p>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <p className="text-xl font-bold text-gray-700">{nav?.totalHazardsReported || 0}</p>
              <p className="text-xs text-gray-500">Hazards Reported</p>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <p className="text-xl font-bold text-gray-700">{nav?.totalReferrals || 0}</p>
              <p className="text-xs text-gray-500">Total Referrals</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Waitlist & Hazards */}
      <div className="grid grid-cols-2 gap-4 mt-6">
        <Card>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Waitlist</p>
              <p className="text-2xl font-bold">{nav?.waitlistCount || 0}</p>
            </div>
            <UserPlus className="h-8 w-8 text-gray-300" />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Active Hazards</p>
              <p className="text-2xl font-bold">{nav?.activeHazards || 0}</p>
            </div>
            <AlertTriangle className="h-8 w-8 text-gray-300" />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
